import React, { useState, useCallback } from 'react';

/**
 * Hook to manage sorting state and logic for DataGrid
 * 
 * @param {boolean} enabled - Whether sorting is enabled
 * @param {Array} columns - Column definitions
 * @returns {Object} { sortColumns, setSortColumns, applySorting }
 */
export function useDataGridSorting(enabled, columns) {
  const [sortColumns, setSortColumns] = useState([]);

  // Function to sort a subset of rows (e.g., children) - doesn't separate parent/child
  const sortRowsSubset = useCallback(rowsToSort => {
    if (!enabled || sortColumns.length === 0) return rowsToSort;
    const columnsToSort = sortColumns.map(_ref => {
      let {
        columnKey,
        direction
      } = _ref;
      const columnIndex = parseInt(columnKey.replace('col_', ''));
      return {
        column: columns[columnIndex],
        direction
      };
    });
    if (columnsToSort.some(_ref2 => {
      let {
        column
      } = _ref2;
      return !(column !== null && column !== void 0 && column.valueFnForSortAndFilter);
    })) return rowsToSort;

    // Separate totals rows from sortable rows
    const totalsRows = rowsToSort.filter(row => row.isTotalsRow);
    const sortableRows = rowsToSort.filter(row => !row.isTotalsRow);

    // Preserve original indices for stable sorting
    const rowsWithIndices = sortableRows.map((row, index) => ({
      row,
      originalIndex: index
    }));
    const sortedRows = rowsWithIndices.sort((a, b) => {
      // Wrap rows in the expected format for valueFnForSortAndFilter
      const rowA = {
        data: {
          object: a.row
        }
      };
      const rowB = {
        data: {
          object: b.row
        }
      };
      for (const {
        column,
        direction
      } of columnsToSort) {
        const valueA = column.valueFnForSortAndFilter(rowA);
        const valueB = column.valueFnForSortAndFilter(rowB);
        let comparison = 0;
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          comparison = valueA.localeCompare(valueB);
        } else if (typeof valueA === 'number' && typeof valueB === 'number') {
          comparison = valueA - valueB;
        } else {
          // Fallback: convert to string
          comparison = String(valueA).localeCompare(String(valueB));
        }
        if (comparison !== 0) {
          return direction === 'ASC' ? comparison : -comparison;
        }
      }
      return a.originalIndex - b.originalIndex;
    }).map(item => item.row);

    // Put totals rows back at the beginning
    return [...totalsRows, ...sortedRows];
  }, [enabled, sortColumns, columns]);

  // Sort only parent rows (children are sorted separately when inserted)
  const applySorting = useCallback(rows => {
    if (!enabled || sortColumns.length === 0) return rows;

    // Children will be sorted by insertChildRows, so just sort the parent rows here
    rows = sortRowsSubset(rows);
    return rows;
  }, [enabled, sortColumns, sortRowsSubset]);
  return {
    sortColumns,
    setSortColumns,
    applySorting,
    sortRowsSubset
  };
}